import { useMemo } from 'react'
import { IncomingMessage, ServerResponse } from 'http'
import { NextPage } from 'next'
import { Case, Switch } from 'react-if'
import { paths } from '@/constants/paths'
import { projectTypes } from '@/constants/projectTypes'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { TheatricalRegionProvider } from '@/contexts/TheatricalRegionContext/TheatricalRegionContext'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { Seo } from '@/layout/Seo'
import { MovieJsonLd, PodcastSeriesJsonLd, TVSeriesJsonLd } from '@/layout/Seo/JsonLd'
import { getJsonLdForMovie, getJsonLdForPodcastSeries, getJsonLdForTVSeries } from '@/layout/Seo/ProjectSeoHelper'
import { GetAppBannerProvider } from '@/organisms/GetAppBanner'
import { ShopifyConfigProvider } from '@/organisms/HorizontalShopifyMerchScroller/ShopifyConfigContext'
import {
  hasDownloadablesAvailable,
  hasLivestreamsAvailable,
  hasMerchItemsAvailable,
} from '@/organisms/WatchProjectTabs/utils'
import { getPageByPath } from '@/services/CmsService/Pages'
import { getStoriesForProject } from '@/services/CommunityService'
import { CatalogTitle, fetchTitle, mapSeoProps } from '@/services/ContentCatalog'
import { createWatchProjectDeepLink } from '@/services/DeepLinkService/DeepLinkService'
import { getFeatureAnnouncement } from '@/services/FeatureAnnouncementService'
import { LightFranchise } from '@/services/FranchiseService'
import { getGuildStatus, GuildPermissions } from '@/services/GuildUserService/GuildUserService'
import { PlayableLivestream, getPlayableLivestreamsByProject } from '@/services/LivestreamService'
import { getParentalControlsForProject, getSkipsEnabledFromParentalControls } from '@/services/ParentalControlsService'
import { isInGuildAccess, isStreaming } from '@/services/ProjectPhaseService'
import { getContentfulProjectTheme } from '@/services/ProjectThemeService'
import {
  ProjectMeta,
  ResumeWatchingData,
  VideoMetaData,
  WatchableNode,
  WatchableContentWithReasons,
  fetchFranchiseData,
  fetchTheatricalReleaseData,
  fetchTheatricalReleaseMono,
  getCurrentLivestream,
  getMovieAndSpecialsWatchables,
  getProjectAbout,
  getProjectVideos,
  getResumeWatchingForProject,
  getWatchableContentByGuid,
} from '@/services/ProjectsService'
import { WatchProjectProvider } from '@/services/ProjectsService/WatchProjectProvider'
import { normalizeProjectSeasons } from '@/services/ProjectsService/normalizeSeasons'
import { CurrentLivestream } from '@/services/ProjectsService/types'
import { SuggestedShowsAndFilmsContent, getSuggestedShowsAndFilms } from '@/services/RecommendationService'
import { fetchPageDataContext } from '@/services/RenderService'
import { getShopifyConfigurationForProject } from '@/services/ShopifyConfigurationService/ShopifyConfigurationService'
import { Note } from '@/services/StoriesService'
import { getSweepstakes } from '@/services/SweepstakeService/SweepstakeService'
import { User } from '@/services/UserService'
import { WatchlistProvider } from '@/services/WatchlistService/WatchlistProvider'
import { ProjectTheme, TheatricalRegion } from '@/types/codegen-contentful'
import { TicketsStatusReference } from '@/types/codegen-federation'
import { getProjectInEarlyAccess } from '@/utils/GuildUtil'
import { useProjectMetaTitle } from '@/utils/SpecialProjectUtils'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { omitUndefineds } from '@/utils/object'
import { shouldHideProjectFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { loadTranslations } from '@/utils/translate/translate-server'
import { getUser } from '@/utils/users/usersServer'
import { DavidPifCTA } from '@/views/PayItForward/DavidPifView/components/DavidPifCTA'
import { WatchProjectData, WatchProjectView, WatchProjectViewProps } from '@/views/WatchProjectView'

interface WatchProjectPageProps extends WatchProjectViewProps, ProjectMeta {
  supportedTheatricalRegions: TheatricalRegion[]
  theatricalRegion: TheatricalRegion[]
  locale: string
  contentfulProjectTheme: ProjectTheme
  deepLink: string | null
  projectSlug: string
  catalogTitle: CatalogTitle | null
  isProjectInEarlyAccess: boolean
  projectData: WatchProjectData
  isGuildMember?: boolean
  isLoggedIn?: boolean
  franchise?: LightFranchise
  hasWatchableReasons?: boolean
  activeSweepstake?: boolean
  skipsEnabled?: boolean
  experiments: ServerExperiments
  bonusVideos: VideoMetaData[]
  livestreams: PlayableLivestream[]
  communityStories: Note[]
  watchables: WatchableNode[]
  hasLivestreams: boolean
  hasDownloadables: boolean
  hasBonusVideos: boolean
  currentLivestream: CurrentLivestream | null
  suggestedShowsAndFilms: SuggestedShowsAndFilmsContent
  resumeWatchingData: ResumeWatchingData | null
  ticketStatus: TicketsStatusReference | null
  watchableContent: WatchableContentWithReasons
  guildPermissions: GuildPermissions | null
}

export const WatchProjectPage: NextPage<WatchProjectPageProps> = (props) => {
  const { title } = useProjectMetaTitle({ ...props, watchableContent: props.watchableContent })
  const meta = mapSeoProps({
    path: `${paths.watch.index}/${props.slug}`,
    title,
    catalogTitle: props.catalogTitle,
    locale: props.locale,
    projectMeta: props,
  })
  const shouldHide = shouldHideProjectFromSearchEngines(props)
  const hasShopifyConfig = Boolean(
    props.projectType === 'special' ||
      (props.shopifyConfig?.storeUrl && props.shopifyConfig?.accessToken && props.shopifyConfig?.handle),
  )
  const dryBarShopifyFallbacks =
    props.projectType === 'special'
      ? {
          title: props.name,
          storeUrl: 'https://drybarcomedy.myshopify.com/api/2024-04/graphql.json',
          accessToken: '0ed4cb3443788011fd6a56b90286381c',
          handle: 'merch',
          storeHref: 'https://shop.angel.com/pages/show-dry-bar-comedy',
        }
      : undefined
  const hasMerchItems = hasMerchItemsAvailable({
    hasShopifyConfig,
    storeUrl: props.shopifyConfig?.storeUrl ?? dryBarShopifyFallbacks?.storeUrl,
    accessToken: props.shopifyConfig?.accessToken ?? dryBarShopifyFallbacks?.accessToken,
    handle: props.shopifyConfig?.handle ?? dryBarShopifyFallbacks?.handle,
    storeHref: props.shopifyConfig?.storeHref ?? dryBarShopifyFallbacks?.storeHref,
  })

  const davidPifCtaExpValue = useMemo(() => {
    const youngDavidExpValue = props.experiments?.['david-cta-watch-young-david']
    const davidExpValue = props.experiments?.['david-cta-watch-david']

    if (youngDavidExpValue && youngDavidExpValue !== 'OFF') return youngDavidExpValue
    if (davidExpValue && davidExpValue !== 'OFF') return davidExpValue

    return 'OFF'
  }, [props.experiments])

  return (
    <>
      <Seo {...meta} nofollow={shouldHide} noindex={shouldHide} path={`${paths.watch.index}/${props.slug}`} />
      <Switch>
        <Case condition={props.projectType === projectTypes.movie}>
          <MovieJsonLd {...getJsonLdForMovie(props)} />
        </Case>
        <Case condition={props.projectType === projectTypes.podcast}>
          <PodcastSeriesJsonLd {...getJsonLdForPodcastSeries(props)} />
        </Case>
        <Case condition={props.projectType === projectTypes.series || props.projectType === projectTypes.special}>
          <TVSeriesJsonLd {...getJsonLdForTVSeries(props)} />
        </Case>
      </Switch>
      <ShopifyConfigProvider
        storeUrl={props.shopifyConfig?.storeUrl ?? dryBarShopifyFallbacks?.storeUrl}
        accessToken={props.shopifyConfig?.accessToken ?? dryBarShopifyFallbacks?.accessToken}
        handle={props.shopifyConfig?.handle ?? dryBarShopifyFallbacks?.handle}
        storeHref={props.shopifyConfig?.storeHref ?? dryBarShopifyFallbacks?.storeHref}
      >
        <ProjectMetaProvider project={props}>
          <TheatricalRegionProvider
            supportedTheatricalRegions={props.supportedTheatricalRegions}
            regionData={props.theatricalRegion}
          >
            <GetAppBannerProvider deepLink={props.deepLink} projectSlug={props.projectSlug} context="watch">
              <WatchProjectProvider
                bonusVideos={props.bonusVideos}
                livestreams={props.livestreams}
                downloadables={props.downloadables}
                communityStories={props.communityStories}
                watchables={props.watchables}
                hasLivestreams={props.hasLivestreams}
                hasDownloadables={props.hasDownloadables}
                hasMerchItems={hasMerchItems}
                hasBonusVideos={props.hasBonusVideos}
                currentLivestream={props.currentLivestream}
                ticketStatus={props.ticketStatus ?? null}
                watchableContent={props.watchableContent}
                guildPermissions={props.guildPermissions}
                isGuildMember={props.isGuildMember}
              >
                <WatchlistProvider>
                  <WatchProjectView {...props} />
                </WatchlistProvider>
              </WatchProjectProvider>
            </GetAppBannerProvider>

            {davidPifCtaExpValue && davidPifCtaExpValue !== 'OFF' && (
              <DavidPifCTA
                isStarburst={davidPifCtaExpValue === 'STARBURST'}
                page="watch"
                projectSlug={props.projectSlug}
              />
            )}
          </TheatricalRegionProvider>
        </ProjectMetaProvider>
      </ShopifyConfigProvider>
    </>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<WatchProjectPageProps>(
  async ({ federationClient, locale, next404, params, req, res, preview, userId }) => {
    const projectSlug = getItemOrFirstEntry(params.project)
    if (!projectSlug) return next404()

    const theatricalSlug = projectSlug
    const shouldIncludePreview = isDevelopmentEnvironment()
    const path = `${paths.watch.index}/${projectSlug}`

    // Start independent calls in parallel with projectMeta fetch
    const [
      projectMeta,
      shopifyConfig,
      page,
      contentfulProjectTheme,
      user,
      translations,
      guildStatus,
      bonusVideos,
      livestreams,
      communityStories,
      movieSpecialWatchables,
      resumeWatchingData,
    ] = await Promise.all([
      getProjectAbout({ slug: projectSlug }, federationClient, {
        locale,
        preview,
        includeSeasons: true,
      }),
      getShopifyConfigurationForProject(projectSlug, { locale, preview }),
      getPageByPath(`${paths.watch.index}/${projectSlug}`, { preview, locale }),
      getContentfulProjectTheme(projectSlug, preview),
      getUser(federationClient, userId),
      loadTranslations(locale, [
        'common',
        'home',
        'watch',
        'app-promo',
        'ads',
        'account',
        'guild',
        'theatrical-presales',
      ]),
      getGuildStatus(federationClient),
      getProjectVideos({
        projectSlug,
        page: 'bonus',
        client: federationClient,
      }),
      getPlayableLivestreamsByProject({ projectSlug }, federationClient),
      getStoriesForProject({
        projectSlug,
        client: federationClient,
      }),
      getMovieAndSpecialsWatchables(projectSlug, federationClient),
      getResumeWatchingForProject(userId, projectSlug, federationClient),
    ])

    if (!projectMeta || !projectMeta.id) {
      return next404()
    }

    // Fetch data that depends on projectMeta
    const [franchise, watchableContent, parentalControls, currentLivestream, suggestedShowsAndFilms] =
      await Promise.all([
        fetchFranchiseData(projectMeta.franchiseDetails?.slug),
        getWatchableContentByGuid(projectMeta?.title?.id, federationClient),
        getParentalControlsForProject({ client: federationClient, contentId: projectMeta?.title?.id }),
        getCurrentLivestream(projectMeta.id, federationClient),
        getSuggestedShowsAndFilms(projectMeta?.title?.id ?? '', federationClient),
      ])

    const skipsEnabled = getSkipsEnabledFromParentalControls(parentalControls?.getContent)
    const hasStreamableContent = isStreaming(watchableContent)

    // Only fetch sweepstakes for movies without streamable content (same condition as theatricalReleaseMono)
    const shouldFetchTheatricalData = projectMeta.projectType === projectTypes.movie && !hasStreamableContent

    const [
      theatricalRelease,
      theatricalReleaseMono,
      sweepstakes,
      deepLink,
      experiments,
      pageDataContext,
      catalogTitle,
    ] = await Promise.all([
      fetchTheatricalReleaseData({
        projectType: projectMeta.projectType,
        theatricalSlug,
        shouldIncludePreview,
        locale,
      }),
      fetchTheatricalReleaseMono({
        projectType: projectMeta.projectType,
        theatricalSlug,
        hasStreamableContent,
        client: federationClient,
      }),
      shouldFetchTheatricalData ? getSweepstakes(federationClient, ['LIVE']) : null,
      createWatchProjectDeepLink(
        {
          path,
          projectSlug,
          projectName: projectMeta?.name,
          contentName: projectMeta?.name,
          audioLocale: locale,
          captionLocale: locale,
        },
        {
          campaign: 'prompt-app-install',
          feature: 'app-prompt',
          channel: 'angel-web',
        },
      ),
      runExperiments({ req, res, skipsEnabled, projectSlug }),
      page ? fetchPageDataContext(page, preview, locale, federationClient) : null,
      projectMeta.contentCatalogId ? fetchTitle(locale, projectMeta.contentCatalogId) : null,
    ])

    const redirect = getRedirect(projectSlug, user, projectMeta)
    if (redirect) return redirect

    const regionData = theatricalRelease?.regionCollection ?? []
    const supportedTheatricalRegions = theatricalRelease?.supportedTheatricalRegions ?? []

    // Normalize projectMeta to populate seasons from title.seasons
    const normalizedProjectData = normalizeProjectSeasons(projectMeta) ?? { ...projectMeta, seasons: [] }

    const isProjectInEarlyAccess = computeProjectEarlyAccess(
      normalizedProjectData,
      watchableContent,
      guildStatus?.isGuildMember,
    )
    const hasWatchableReasons = Boolean(watchableContent?.watchableReasons?.length)
    const activeSweepstake = computeActiveSweepstake(sweepstakes, theatricalReleaseMono?.theatricalSlug)
    const hasLivestreams = hasLivestreamsAvailable({ livestreams })
    const hasDownloadables = hasDownloadablesAvailable(projectMeta.downloadables)
    const hasBonusVideos = computeHasBonusVideos(bonusVideos, movieSpecialWatchables, projectMeta.projectType)

    return {
      props: {
        locale,
        shopifyConfig,
        theatricalRegion: regionData,
        supportedTheatricalRegions,
        projectSlug,
        deepLink,
        ...projectMeta,
        featureAnnouncement: getFeatureAnnouncement(params?.project as string),
        page,
        pageDataContext,
        contentfulProjectTheme,
        catalogTitle: omitUndefineds(catalogTitle) ?? null,
        isProjectInEarlyAccess,
        projectData: normalizedProjectData,
        hasWatchableReasons,
        franchise: omitUndefineds(franchise),
        isGuildMember: Boolean(guildStatus && guildStatus?.isGuildMember),
        isLoggedIn: Boolean(userId),
        activeSweepstake,
        skipsEnabled,
        experiments,
        bonusVideos: bonusVideos ?? [],
        livestreams: livestreams ?? [],
        communityStories: communityStories ?? [],
        watchables: movieSpecialWatchables?.watchables ?? [],
        hasLivestreams,
        hasDownloadables,
        hasBonusVideos,
        currentLivestream,
        suggestedShowsAndFilms: suggestedShowsAndFilms ?? [],
        resumeWatchingData,
        ticketStatus: theatricalReleaseMono?.region?.ticketsStatus ?? null,
        watchableContent,
        guildPermissions: getGuildPermissionsFromStatus(guildStatus),
        ...translations,
      },
    }
  },
)

export default WatchProjectPage

const getRedirect = (projectSlug: string, user: User, projectMeta: ProjectMeta) => {
  if (!user?.isAdmin && !projectMeta?.public) {
    return {
      redirect: {
        destination: paths.watch.index,
        permanent: false,
      },
    }
  }

  if (projectSlug === slugs.truthAndConviction) {
    return {
      redirect: {
        destination: paths.watch.truthAndTreason,
        permanent: false,
      },
    }
  }

  if (projectSlug === 'the-last-rodeo') {
    return {
      redirect: {
        destination: paths.watch.theLastRodeo,
        permanent: false,
      },
    }
  }

  return null
}

const computeHasBonusVideos = (
  bonusVideos: VideoMetaData[] | null,
  movieSpecialWatchables: { watchables?: WatchableNode[] | null } | null,
  projectType: string | null | undefined,
) => {
  const watchables = movieSpecialWatchables?.watchables ?? []

  // Filter out the main content type that will be excluded in the UI
  // Movies filter out MOVIE watchables, specials filter out SPECIAL watchables
  const bonusWatchables = watchables.filter((w) => {
    if (projectType === 'movie' && w?.__typename === 'ContentMovie') return false
    if (projectType === 'special' && w?.__typename === 'ContentSpecial') return false
    return true
  })

  const hasBonusWatchables = bonusWatchables.length > 0
  return Boolean((bonusVideos && bonusVideos.length > 0) || hasBonusWatchables)
}

const computeActiveSweepstake = (
  sweepstakes: Array<{ slug?: string | null } | null> | null,
  theatricalSlug?: string | null,
) => {
  if (!theatricalSlug) return false
  return Boolean(sweepstakes?.some((sweepstake) => sweepstake?.slug === theatricalSlug))
}

const computeProjectEarlyAccess = (
  projectData: WatchProjectData | null,
  watchableContent: Parameters<typeof isInGuildAccess>[0],
  isGuildMember?: boolean | null,
) => {
  if (!projectData) return false
  return Boolean(isInGuildAccess(watchableContent) || getProjectInEarlyAccess(projectData, Boolean(isGuildMember)))
}

const getGuildPermissionsFromStatus = (
  guildStatus: Awaited<ReturnType<typeof getGuildStatus>> | null,
): GuildPermissions | null => {
  return guildStatus?.guildPermissions ?? null
}

async function runExperiments({
  req,
  res,
  skipsEnabled,
  projectSlug,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  skipsEnabled: boolean
  projectSlug: string
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = []

  if (skipsEnabled && projectSlug !== slugs.sketch) {
    expConfigs.push({ experimentKey: 'family-friendly-audio-support', defaultValue: false })
  }
  expConfigs.push({
    experimentKey: 'david-cta-watch-young-david',
    defaultValue: 'OFF',
    skip: projectSlug !== slugs.youngDavid,
  })
  expConfigs.push({
    experimentKey: 'david-cta-watch-david',
    defaultValue: 'OFF',
    skip: projectSlug !== slugs.david,
  })

  const experiments = await runExperimentsOnServer(req, res, expConfigs)

  return experiments
}
